import React from 'react';

/**
 * Source unique pour le calcul et l'affichage des variations période à période.
 *
 * Avant, quarante-cinq fichiers en portaient leur propre copie, réparties en douze
 * variantes : les unes affichaient un `0 %` en rouge, les autres en vert, et cinq
 * d'entre elles pouvaient afficher « NaN% » faute de garde. La sémantique vit
 * désormais ici ; seule la présentation reste paramétrable, pour que chaque
 * tableau garde la taille et l'espacement qu'il avait.
 */

/**
 * Marqueur « il n'y avait rien avant » — **pas un pourcentage**.
 *
 * Passer de 0 à 955 clics n'a pas de taux de croissance : la division par zéro n'est pas
 * grande, elle est indéfinie. L'accueil affichait pourtant « ↑ 999,0 % », un chiffre que
 * personne n'a calculé et que rien ne distinguait d'une vraie croissance de 999 % — un passage
 * de 100 à 1 099, qui existe.
 *
 * La valeur est hors d'atteinte de tout pourcentage réel : un afficheur qui l'ignorerait se
 * trahirait aussitôt, au lieu de la faire passer pour un résultat plausible. Son pendant côté
 * serveur est `backend/src/lib/variation.js`, qui la produit.
 */
export const DEPUIS_ZERO = Number.MAX_SAFE_INTEGER;

export const VARIATION_COLORS = {
  up: '#10b981',
  down: '#ef4444',
  flat: '#6b7280'
};

/**
 * Variation en pourcentage entre deux valeurs.
 * Deux zéros ne varient pas ; partir de zéro vaut ±100 %.
 */
export const calculateVariation = (current, previous) => {
  // Valeur courante absente ou non calculable : rien à afficher.
  if (!Number.isFinite(current)) return NaN;
  // Pas de base de comparaison (zéro, absente, ou division par zéro en amont) :
  // on retombe sur la convention ±100 %.
  if (!Number.isFinite(previous) || previous === 0) {
    return current > 0 ? 100 : current < 0 ? -100 : 0;
  }
  return ((current - previous) / previous) * 100;
};

/**
 * Couleur d'une variation.
 *
 * `inverted` désigne les métriques où une baisse est un progrès : les coûts (CPA, COS,
 * CPC) et le taux de rebond. Une variation nulle reste neutre, jamais verte.
 *
 * @param {number}  variation  variation en pourcentage
 * @param {boolean} inverted   vrai si une hausse est mauvaise
 */
export const variationColor = (variation, inverted = false) => {
  const sign = variation > 0 ? 1 : variation < 0 ? -1 : 0;
  if (sign === 0) return VARIATION_COLORS.flat;
  const isGood = inverted ? sign < 0 : sign > 0;
  return isGood ? VARIATION_COLORS.up : VARIATION_COLORS.down;
};

/**
 * Construit un afficheur de variation.
 *
 * @param {string}   fontSize  taille du texte (défaut : 0.75em)
 * @param {object}   style     surcharges de style fusionnées après les valeurs par défaut
 * @param {function} format    mise en forme de la valeur absolue (défaut : une décimale)
 *
 * L'afficheur renvoyé prend (variation, inverted) : `inverted` désigne les
 * métriques où une baisse est un progrès (CPA, COS, CPC). Il n'inverse que la
 * couleur — la flèche suit toujours le signe réel.
 */
export const makeRenderVariation = ({ fontSize = '0.75em', style = {}, format } = {}) =>
  (variation, inverted = false) => {
    if (variation === null || variation === undefined || isNaN(variation)) return null;

    /*
     * « Partir de zéro » se dit en toutes lettres, pas en pourcentage.
     *
     * La flèche et la couleur restent : l'apparition d'un volume est une bonne nouvelle sur une
     * mesure de volume, une mauvaise sur un coût — c'est ce que `inverted` sait déjà.
     */
    if (variation === DEPUIS_ZERO) {
      return (
        <span
          style={{ display: 'block', fontSize, marginTop: '2px', ...style, color: variationColor(1, inverted) }}
          title="Aucun volume sur la période de référence : la variation n'est pas calculable."
        >
          ↑ nouveau
        </span>
      );
    }

    const sign = variation > 0 ? 1 : variation < 0 ? -1 : 0;
    const arrow = sign > 0 ? '↑' : sign < 0 ? '↓' : '→';
    const color = variationColor(variation, inverted);

    const value = format ? format(Math.abs(variation)) : Math.abs(variation).toFixed(1);

    return (
      <span style={{ display: 'block', fontSize, marginTop: '2px', ...style, color }}>
        {arrow} {value}%
      </span>
    );
  };

/** Afficheur par défaut : bloc, 0.75em — la présentation majoritaire. */
export const renderVariation = makeRenderVariation();
